import React from 'react';

const TermsOfService = () => {
  return (
    <div>
      <h1>PUZZLIFY - TERMS OF SERVICE</h1>
      <p><em>Last updated: 8 June 2025</em></p>

      <p>
        Welcome to <strong>Puzzlify</strong> (“<strong>Puzzlify</strong>,” “<strong>Mountainsoft LLC</strong>,”
        “<strong>we</strong>,” “<strong>our</strong>,” or “<strong>us</strong>”). Puzzlify is a
        product and online chess training service owned and operated by <strong>Mountainsoft LLC</strong>,
        a United States limited liability company. These Terms of Service (“<strong>Terms</strong>”)
        form a legally binding agreement between you (“<strong>you</strong>” or “<strong>User</strong>”) and
        Mountainsoft LLC and govern your access to and use of the Puzzlify website, apps, and any
        related services (collectively, the “<strong>Service</strong>”). By visiting or using the Service,
        you agree to be bound by these Terms and by our&nbsp;
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </p>

      <p>
        When these Terms refer to “Puzzlify,” that reference includes Mountainsoft LLC as the legal
        entity responsible for delivering the Service, processing payments, and handling your data.
      </p>

      <hr />

      <h2>1.&nbsp;Acceptance of Terms</h2>
      <p>
        If you do not agree to every provision of these Terms, do not access or use the Service.
        By accepting, you acknowledge that your contract is with Mountainsoft LLC, doing business as
        Puzzlify. We may update the Terms at any time by posting the revised version online. Continued
        use after changes become effective constitutes acceptance.
      </p>

      <h2>2.&nbsp;Eligibility</h2>
      <p>
        You must be at least <strong>13 years old</strong> (or the minimum digital consent age in
        your country) to create an account or purchase a subscription. By using the Service,
        you affirm that you meet this requirement and have legal capacity to enter these Terms.
      </p>

      <h2>3.&nbsp;Accounts &amp; Security</h2>
      <ul>
        <li>You must provide a valid e‑mail address and keep account information accurate.</li>
        <li>You are responsible for all activity that occurs under your login credentials.</li>
        <li>Notify us immediately of any unauthorized use.</li>
      </ul>

      <h2>4.&nbsp;Subscriptions, Billing &amp; Refunds</h2>
      <p><strong>Plans.</strong> We currently offer a Monthly subscription and an Annual subscription. All subscriptions renew automatically unless canceled at least 24 hours before the end of the then‑current term.</p>
      <p><strong>Renewal Notices.</strong> We send a reminder email (a) at least 3 days before each renewal and (b) on the renewal date.</p>
      <p><strong>Billing.</strong> You authorize us (or our payment processor) to charge your selected payment method at the stated price plus any taxes.</p>
      <p><strong>Refunds.</strong> Payments are non-refundable except where required by law. However, we may grant refunds <strong>case‑by‑case</strong> at our sole discretion. Requests should be sent to&nbsp;<a href="mailto:puzzlify@puzzlify.io">puzzlify@puzzlify.io</a>.</p>

      <h2>5.&nbsp;Free Tier</h2>
      <p>
        Puzzlify offers a free tier with limited daily puzzles and basic statistics. Free accounts
        do not show advertisements at this time, but we reserve the right to introduce ads in the
        future.
      </p>

      <h2>6.&nbsp;License &amp; Intellectual Property</h2>
      <ul>
        <li><strong>Your license.</strong> We grant you a limited, nonexclusive, revocable, non-transferable license to access and use the Service for personal, non‑commercial chess training.</li>
        <li><strong>Our property.</strong> The Service, software, graphics, trademarks, and all puzzle curation logic are owned by Mountainsoft LLC or its licensors and are protected by intellectual‑property laws.</li>
        <li><strong>Puzzle sources.</strong> Chess positions contained in the Service derive in part from the Lichess puzzle dataset, released under the Creative Commons CC0 (public domain) license.</li>
      </ul>

      <h2>7.&nbsp;Prohibited Conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Resell, sublicense, or exploit the Service or content commercially.</li>
        <li>Scrape, harvest, or reverse engineer puzzles or site code.</li>
        <li>Use bots, engines, or any automated system to bypass usage limits.</li>
        <li>Upload viruses or interfere with servers or networks.</li>
        <li>Violate any applicable law.</li>
      </ul>

      <h2>8.&nbsp;Third Party Content &amp; Links</h2>
      <p>
        The Service may reference third party chess sites, engines, or code libraries. Puzzlify is
        not responsible for third party content or practices.
      </p>

      <h2>9.&nbsp;Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” Mountainsoft LLC MAKES <strong>NO GUARANTEES</strong>
        REGARDING UNINTERRUPTED ACCESS, ERROR FREE OPERATION, OR SPECIFIC CHESS RATING
        IMPROVEMENT. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
        EXPRESS OR IMPLIED.
      </p>

      <h2>10.&nbsp;Limitation of Liability</h2>
      <p>
        To the fullest extent allowed by law, Mountainsoft LLC's total liability for any claim arising
        out of or relating to the Service shall not exceed the amount you paid us in the
        <strong>preceding 12 months</strong> (or USD $50 if you have not paid). IN NO EVENT SHALL
        Mountainsoft LLC BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
      </p>

      <h2>11.&nbsp;Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Mountainsoft LLC (including its members, managers, and affiliates) from any claims, damages, and expenses
        (including attorneys' fees) arising from your use of the Service or violation of these Terms.
      </p>

      <h2>12.&nbsp;Termination</h2>
      <p>
        We may suspend or terminate your account immediately for any violation of these Terms,
        non-payment, or suspected fraudulent activity. Upon termination, all licenses granted
        to you cease.
      </p>

      <h2>13.&nbsp;Governing Law &amp; Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of the <strong>United States and the State of New York</strong>,
        without regard to conflict-of-law principles. <strong>Informal resolution first:</strong>
        You agree to contact Mountainsoft LLC at&nbsp;<a href="mailto:puzzlify@puzzlify.io">puzzlify@puzzlify.io</a>&nbsp;to attempt
        to resolve any dispute. If we cannot resolve it within 30 days, the dispute will be
        <strong>resolved by binding arbitration</strong> administered by the American Arbitration
        Association under its Consumer Arbitration Rules. Either party may bring an individual
        claim in small claims court instead of arbitration. <strong>Class-action waiver:</strong>
        Disputes must be brought on an individual basis; class or representative proceedings
        are not allowed.
      </p>

      <h2>14.&nbsp;Changes to the Service</h2>
      <p>
        We may modify or discontinue features at any time. If a change materially reduces core
        functionality for paid Users, we will provide at least 30 days' notice and an option to
        cancel with a prorated refund.
      </p>

      <h2>15.&nbsp;Contact</h2>
      <p>
        Questions about these Terms or the Service?<br />
        <strong>Email:</strong>&nbsp;<a href="mailto:puzzlify@puzzlify.io">puzzlify@puzzlify.io</a><br />
        <strong>Legal entity:</strong>&nbsp;Mountainsoft LLC, doing business as Puzzlify<br />
        <strong>Postal mail:</strong> (This email serves as our principal contact.)
      </p>

      <hr />

      <p>
        By using Puzzlify you acknowledge that you have read, understood, and agree to these
        Terms of Service.
      </p>

    </div>
  );
};

export default TermsOfService;
